// © Microsoft Corporation. All rights reserved.

import { IDataConnector, PageBase } from "@msnews/experiences-redux";
import { NavigationDataConnector, NavigationItems } from "@msnews/navigation-data-connector";
import { TopicDataActions, TopicDataConnector } from "@msnews/topics-shared-state";

import { ChildExperienceConfigInfo } from "@msnews/core";
import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { appEnvironment } from "@msnews/core";
import { waitForAboveTheFoldVisuallyReadyAsync } from "@msnews/milestone-data";
import { DynamicPivotsSourceType, PeregrineRoute } from "./Interfaces";
import { getRoutePath } from "./Utilities";

/**
 * Class to handle getting dynamic pivots from different data sources
 */
export default class DynamicRouteProvider {
    /** Callback for when the dynamic routes are updated */
    private onRouteUpdateCallback: (dynamicRoutes: PeregrineRoute[]) => void;

    /** Data connector to be used for the dynamic pivots */
    private connector: IDataConnector<any, any>;

    /** Config index href to render the dynamic pivots */
    private dynamicRouteExperienceInfo: ChildExperienceConfigInfo;

    constructor(onRouteUpdateCallback: (dynamicRoutes: PeregrineRoute[]) => void, dynamicRouteExperienceInfo?: ChildExperienceConfigInfo) {
        this.onRouteUpdateCallback = onRouteUpdateCallback;
        this.dynamicRouteExperienceInfo = dynamicRouteExperienceInfo;
    }

    /**
     * Gets the dynamic pivots and sets up necessary listeners for updates to the dynamic pivots
     * @param dataConnector name of the data connector to be used
     */
    public async getDynamicPivotsFromDataConnector(dataConnector: ConnectorNamespaces): Promise<void> {
        switch (dataConnector) {
            case ConnectorNamespaces.TopicData:
                await this.getPivotsFromTopicData();
                break;

            case ConnectorNamespaces.NavigationData:
                await this.getPivotsFromNavigationData();
                break;

            default:
                return;
        }
    }

    // #region Topics data pivots

    /**
     * Helper function to get pivots from topics data connector
     */
    private async getPivotsFromTopicData(): Promise<void> {
        await waitForAboveTheFoldVisuallyReadyAsync();

        PageBase.getInstance().rootReducer.getDataConnector(ConnectorNamespaces.TopicData).then((topicDataConnector: TopicDataConnector) => {
            this.connector = topicDataConnector;
            (this.connector as TopicDataConnector).getFollowedTopics(false).then(() => {
                this.onUpdateFollowedTopics();
            });
        });

        // Subscribe to changes in followed topics
        TopicDataActions.followedTopics.registerObserver(this.onUpdateFollowedTopics);
        TopicDataActions.updateTopicFollowedState.registerObserver(this.onUpdateFollowedTopics);
    }

    /**
     * Update the dynamic pivots when the followed topics are updated.
     * Only used if the connector is a topics data connector
     */
    private onUpdateFollowedTopics = () => {
        if (!this.connector) {
            return this.onRouteUpdateCallback([]);
        }

        const topicsDataConnectorState = this.connector.getCurrentState();
        const followedTopicIds = topicsDataConnectorState.followedTopicIds;
        const topicMap = topicsDataConnectorState.topicMap;
        const newDynamicOrderedRoutePaths = [];

        if (followedTopicIds && topicMap) {
            followedTopicIds.forEach(topicId => {
                const topic = topicMap[topicId];
                const dynamicRoute: PeregrineRoute = {
                    path: getRoutePath(topic.name || topic.canonicalName, topicId),
                    display: topic.name,
                    experienceInfo: this.dynamicRouteExperienceInfo,
                    context: { feedId: topicId },
                    pivotId: topicId,
                    dynamicPivotSource: DynamicPivotsSourceType.TopicsData
                };

                newDynamicOrderedRoutePaths.push(dynamicRoute);
            });
        }

        this.onRouteUpdateCallback(newDynamicOrderedRoutePaths);
    };

    // #endregion

    // #region Navigation data pivots

    /**
     * Helper function to get pivots from navigation data connector
     */
    private async getPivotsFromNavigationData(): Promise<void> {
        this.connector = await PageBase.getInstance().rootReducer.getDataConnector(ConnectorNamespaces.NavigationData) as NavigationDataConnector;

        if (this.connector) {
            const navigationItems: NavigationItems = await (this.connector as NavigationDataConnector).getNavigationItems(appEnvironment.HostPage.verticalKey, appEnvironment.HostPage.categoryKey);
            this.onRouteUpdateCallback(this.mapNavigationItemsToDynamicRoutes(navigationItems));
        } else {
            this.onRouteUpdateCallback([]);
        }
    }

    /**
     * Maps navigation items to dynamic routes.
     * Only used if the connector is a navigation data connector
     * @param navigationItems Maps navigation items to dynamic routes
     */
    private mapNavigationItemsToDynamicRoutes(navigationItems: NavigationItems): PeregrineRoute[] {
        return navigationItems.map(navigationItem =>
            ({
                path: navigationItem.targetUrl,
                externalUrl: navigationItem.targetUrl,
                display: navigationItem.displayText,
                pivotId: navigationItem.categoryKey,
                dynamicPivotSource: DynamicPivotsSourceType.NavigationData
            })
        );
    }

    // #endregion
}
